import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "../lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        danger:
          "border-red-500/25 bg-red-500/12 text-red-700 dark:border-red-500/35 dark:bg-red-500/16 dark:text-red-300",
        success:
          "border-green-500/25 bg-green-500/12 text-green-700 dark:border-green-500/35 dark:bg-green-500/16 dark:text-green-300",
        warning:
          "border-yellow-500/25 bg-yellow-500/12 text-yellow-700 dark:border-yellow-500/35 dark:bg-yellow-500/16 dark:text-yellow-300",
        info:
          "border-border bg-muted text-muted-foreground dark:bg-input/30 dark:text-foreground/80",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
      },
      size: {
        default: "text-xs",
        sm: "text-xs",
        lg: "text-sm",
        xs: "text-xs",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"
  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
      ref={props.ref as React.Ref<HTMLSpanElement> | undefined}
    />
  )
}

export { Badge, badgeVariants }
